import React from 'react';

import Avatar, { type AvatarPropTypes } from '@atlaskit/avatar/avatar';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import type { SizeType } from '@atlaskit/avatar/types';
import { token } from '@atlaskit/tokens';

import { TeamAvatarImage } from './teams-avatar-image';

/*
 * The component accepts src and teamId as input params. Order of preference:
 * If src is provided, use that directly.
 * Else if teamId is provided, use that to generate the static image URL.
 * Else fallback.
 */
/**
 * The set of avatar sizes supported by `TeamAvatar`.
 *
 * `TeamAvatar` intentionally does not support the `UNSAFE_xsmall` (20px) avatar
 * size: there is no dedicated 20px team-avatar square artwork.
 *
 * TODO(HDS-4629): `UNSAFE_xsmall` is a temporary, private avatar size that will
 * eventually be renamed (`xsmall` -> `xxsmall`, `UNSAFE_xsmall` -> `xsmall`).
 * When that migration happens, revisit whether `TeamAvatar` should support it.
 * Cleanup tracked at:
 * https://hello.atlassian.net/wiki/spaces/DST/pages/7289318824
 */
export type TeamAvatarSize = Exclude<SizeType, 'UNSAFE_xsmall'>;

export type TeamAvatarProps = Omit<AvatarPropTypes, 'appearance' | 'size'> & {
	teamId?: string;
	compact?: boolean;
	size?: TeamAvatarSize;
};

export const ICON_BACKGROUND: 'var(--ds-icon-inverse)' = token('color.icon.inverse');
export const ICON_COLOR: 'var(--ds-icon-subtle)' = token('color.icon.subtle');

export default function TeamAvatar({
	testId,
	src,
	size = 'medium',
	teamId = '',
	compact = false,
	...props
}: TeamAvatarProps): React.JSX.Element {
	// Strip ARI in case the teamId was given in that format
	teamId = teamId.replace('ari:cloud:identity::team/', '');

	return (
		<Avatar {...props} appearance="square" size={size} testId={`${testId}-team-avatar`}>
			<AvatarContent>
				<TeamAvatarImage key={teamId} src={src} testId={testId} teamId={teamId} />
			</AvatarContent>
		</Avatar>
	);
}
